import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { OverviewData, WebsiteOverviewData } from '../../types/overview';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

interface RevenueChartProps {
  data: OverviewData | null;
  websiteData: WebsiteOverviewData | null;
  isLoading: boolean;
  showVariation: boolean;
  onNetworkClick: (networkId: number, networkName: string) => void;
  onBackToNetworks: () => void;
}

export function RevenueChart({ data, websiteData, isLoading, showVariation, onNetworkClick, onBackToNetworks }: RevenueChartProps) {
  const { t } = useTranslation();

  // Déterminer si on affiche les networks ou les websites
  const isWebsiteView = !!websiteData;
  const currentData = isWebsiteView ? websiteData : data;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {isWebsiteView ? `Revenue from ${websiteData?.networkName}` : 'Revenue by Network'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array(5).fill(0).map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-24 h-4 bg-muted rounded animate-pulse" />
                <div className="flex-1 h-8 bg-muted rounded animate-pulse" />
                <div className="w-16 h-4 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentData || (isWebsiteView ? websiteData!.websites.length === 0 : data!.networks.length === 0)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isWebsiteView && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBackToNetworks}
                className="p-1"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {isWebsiteView ? `Revenue from ${websiteData?.networkName}` : 'Revenue by Network'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('overview.noData')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculer la valeur maximale pour normaliser les barres
  const items = isWebsiteView ? websiteData!.websites : data!.networks;
  const maxRevenue = Math.max(...items.map(item => item.revenue));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isWebsiteView && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBackToNetworks}
                className="p-1"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <span>
              {isWebsiteView ? `Revenue from ${websiteData?.networkName}` : 'Revenue by Network'}
            </span>
          </div>
          <span className="text-sm font-normal text-muted-foreground">
            {currentData.period.label} • {items.length} {isWebsiteView ? 'websites' : 'networks'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {items.map((item, index) => {
            const widthPercent = (item.revenue / maxRevenue) * 100;
            const hasVariation = showVariation && item.variation !== undefined;
            
            return (
              <div 
                key={isWebsiteView ? `website-${index}` : `network-${(item as any).networkId}`} 
                className="space-y-2"
              >
                {/* Nom et revenus */}
                <div className="flex items-center justify-between text-sm">
                  <span 
                    className={`font-medium text-foreground ${!isWebsiteView ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
                    onClick={() => {
                      if (!isWebsiteView && 'networkId' in item) {
                        onNetworkClick((item as any).networkId, (item as any).networkName);
                      }
                    }}
                  >
                    {isWebsiteView ? (item as any).websiteName : (item as any).networkName}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: item.currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(item.revenue)}
                    </span>
                    {hasVariation && (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        item.variation! >= 0 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {item.variation! >= 0 ? '+' : ''}
                        {item.variation!.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Barre de progression */}
                <div className="relative">
                  <div className="w-full bg-muted rounded-full h-6">
                    <div 
                      className={`h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2 ${
                        index === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                        index === 1 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                        index === 2 ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                        index === 3 ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                        index === 4 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                        index === 5 ? 'bg-gradient-to-r from-indigo-500 to-indigo-600' :
                        index === 6 ? 'bg-gradient-to-r from-pink-500 to-pink-600' :
                        index === 7 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                        index === 8 ? 'bg-gradient-to-r from-teal-500 to-teal-600' :
                        index === 9 ? 'bg-gradient-to-r from-cyan-500 to-cyan-600' :
                        index === 10 ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' :
                        index === 11 ? 'bg-gradient-to-r from-rose-500 to-rose-600' :
                        index === 12 ? 'bg-gradient-to-r from-violet-500 to-violet-600' :
                        index === 13 ? 'bg-gradient-to-r from-amber-500 to-amber-600' :
                        index === 14 ? 'bg-gradient-to-r from-lime-500 to-lime-600' :
                        'bg-gradient-to-r from-gray-500 to-gray-600'
                      } ${!isWebsiteView ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                      style={{ width: `${Math.max(widthPercent, 5)}%` }}
                      onClick={() => {
                        if (!isWebsiteView && 'networkId' in item) {
                          onNetworkClick((item as any).networkId, (item as any).networkName);
                        }
                      }}
                    >
                      {widthPercent > 20 && (
                        <span className="text-white text-xs font-medium">
                          {((item.revenue / currentData.totalRevenue) * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Légende */}
        <div className="mt-6 pt-4 border-t">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Showing top {items.length} {isWebsiteView ? 'websites' : 'networks'} by revenue
              {!isWebsiteView && ' (click to drill down)'}
            </span>
            {showVariation && (
              <span>Variation vs. previous {currentData.period.label}</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}